import {FC, useEffect, useState} from 'react';
import { useSelector } from 'react-redux';
import { IPayment, IPaymentState } from '../../../../common/state/payments/payment';
// custom
import { IPersonalInfo } from '../../../../common/state/subscriptions/personal_info';
import SimpleSpinner from '../../../../components/loaders/spinners/SimpleSpinner';

export type PersonalInfoFormData = IPersonalInfo;

type IPaymentSelectionProps = {    
    onSubmit: (data: number) => void,
    onBack: () => void
};

const STRIPE_FEE_RATE = 0.03;

const PaymentSelectionForm: FC<IPaymentSelectionProps> = ({onSubmit, onBack}) => {
    // states
    const [optionPrice, setOptionPrice] = useState<number>(0);
    const [otherAmount, setOtherAmount] = useState('');
    const [checkoutPrice, setCheckoutPrice] = useState<number>(0);
    const [selectedCheckBox, setSelectedCheckBox] = useState<string[]>([]);
    const paymentList: IPayment[] = useSelector(({payment} : {
        payment: IPaymentState
    }) => payment.list);

    //evt handlers
    const onToggleCheckBox = (val: number, id: string) => {
        setOtherAmount('');
        if (selectedCheckBox.includes(id)) {
            setSelectedCheckBox(prev => prev.filter(item => item !== id));
            setOptionPrice(prev => prev - val);
        } else {
            setSelectedCheckBox(prev => [...prev, id]);
            setOptionPrice(prev => prev + val);
        }
    };

    // derived
    const stripeFee = checkoutPrice * STRIPE_FEE_RATE;
    const totalPrice = checkoutPrice + stripeFee;

    const onOtherAmountChange = (val: string) => {
        setOtherAmount(val);
        const input = Number(val);
        if (input > 0) {
            setSelectedCheckBox([]);
            setOptionPrice(0);
        }
    };

    // side effects
    useEffect(()=>{
        try {
            const input: number = Number(otherAmount);
            if (input > 0) setCheckoutPrice(input);
            else setCheckoutPrice(optionPrice);
        } catch (err) {
            setCheckoutPrice(optionPrice);
        }
    },[otherAmount, optionPrice]);

    return <section id="form-section"
        className="w-full p-4 my-3 mr-3 bg-white shadow-lg lg:w-3/4 rounded-xl dark:bg-gray-900">
        <h1 className="block mb-2 text-xs font-bold tracking-wide text-gray-700 uppercase dark:text-teal-400">
            2. Payment Selection
        </h1>
        <p className="p-4 my-4 text-gray-700 dark:text-gray-300 dartext-base">
            Please select the payment option or if you want to pay an amount specifically, provide the amount number in the field below <br/>
            <strong>NOTE*: </strong> Promotional offers only apply to the breakdown and not to manual set amount
        </p>
        <section className="grid w-full grid-cols-1 gap-4 mb-6 lg:grid-cols-2">
            {/* Left Side */}
            <div className="flex flex-col w-full px-3">
                <section className='flex flex-col items-center p-4 bg-gray-200 rounded-lg shadow dark:bg-gray-800'>
                    <p className="text-sm font-bold text-gray-700 uppercase dark:text-teal-400">Checkout Price</p>
                    <SimpleSpinner loading={false}/>
                    <p className="text-4xl font-bold text-gray-700 dark:text-gray-100">{checkoutPrice === 0 ? '...' : `$ ${checkoutPrice.toFixed(2)}`}</p>
                    {checkoutPrice > 0 && <>
                        <hr className="w-full my-3 border-gray-300 dark:border-gray-600"/>
                        <p className="text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">Processing Fee (3% Stripe)</p>
                        <p className="text-lg font-semibold text-orange-500">+ $ {stripeFee.toFixed(2)}</p>
                        <hr className="w-full my-3 border-gray-300 dark:border-gray-600"/>
                        <p className="text-xs font-bold text-gray-700 uppercase dark:text-teal-400">Final Price</p>
                        <p className="text-4xl font-bold text-blue-600 dark:text-teal-300">$ {totalPrice.toFixed(2)}</p>
                    </>}
                </section>
            </div>

            {/* Right Side */}
            <div className="w-full px-3">
                <p className="block mb-2 text-xs font-bold tracking-wide text-gray-700 uppercase dark:text-teal-400">
                    Please select from the breakdown below
                </p>
                <section className='flex flex-col'>
                    {paymentList?.map((item: IPayment, index: number) => {
                        const isSelected = selectedCheckBox.includes(item.id);
                        return (
                            <div key={index+1}>
                                <div
                                    onClick={() => onToggleCheckBox(item.amount, item.id)}
                                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all duration-150
                                        ${isSelected
                                            ? 'border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                            : 'border-2 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800'
                                        }`}>
                                    {/* Uniform checkbox */}
                                    <div className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-colors duration-150
                                        ${isSelected
                                            ? 'bg-blue-500 border-blue-500'
                                            : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-500'
                                        }`}>
                                        {isSelected && (
                                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                    <label htmlFor={item.description} className="text-gray-700 cursor-pointer dark:text-gray-200">
                                        <strong className="mr-2 text-pink-600 dark:text-pink-400">USD {item.amount}</strong>
                                        {item.description}
                                    </label>
                                </div>
                                {index < paymentList.length - 1 && (
                                    <hr className="border-gray-200 dark:border-gray-700" />
                                )}
                            </div>
                        );
                    })}
                </section>

                <div className="flex flex-col items-center w-full px-3">
                    <h1 className="my-4 text-2xl font-bold uppercase">OR</h1>
                    <section className='w-full'>
                        <label
                            className="block mb-2 text-xs font-bold tracking-wide text-gray-700 uppercase dark:text-teal-400"
                            htmlFor="grid-other-amount">
                            Paying Other Amount (please specify)...
                        </label>
                        <input
                            onChange={e => onOtherAmountChange(e.target.value)}
                            value={otherAmount}
                            className="block w-full px-4 py-3 mb-3 leading-tight text-gray-700 bg-gray-200 border-2 border-blue-200 rounded appearance-none dark:border-teal-700 focus:outline-none focus:bg-white"
                            id="grid-other-amount" type="text" placeholder="Enter amount"/>
                            <label
                            className="block mb-2 text-xs font-bold tracking-wide text-pink-700"
                            htmlFor="grid-other-amount">
                            {isNaN(Number(otherAmount)) === true ? 'Please specify valid amount (in numbers)': ''}
                        </label>
                    </section>
                </div>
            </div>

            {/* Bottom left */}
            <div className="w-full px-3"></div>
            {/* Bottom Right */}
            <div className="w-full px-3">
                <button
                    onClick={()=>onSubmit(totalPrice)}
                    className="px-4 py-2 mx-3 my-6 font-bold text-white bg-blue-500 border-b-4 border-blue-700 rounded hover:bg-blue-400 hover:border-blue-500">
                    Proceed
                </button>
                <button
                    onClick={()=>onBack()}
                    className="px-4 py-2 mx-3 my-6 font-bold text-gray-700 bg-gray-300 border-b-4 border-gray-500 rounded hover:bg-gray-400 hover:text-white hover:border-gray-700">
                    Back
                </button>
            </div>
        </section>
    </section>;
};

export default PaymentSelectionForm;